import React from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "./components/DrawerTitle";
import { INDUSTRY_FIELD_KEY, SUB_INDUSTRY_FIELD_KEY } from "./const";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { APIFieldItem } from "@/pages/settings/types";
import {
  useEditableSystemFieldForm,
  RENAME_CANCELLED
} from "./hooks/useEditableSystemFieldForm";
import { FieldFormContent } from "./components/FieldFormContent";
import { FieldFormFooter } from "./components/FieldFormFooter";
import RenameValueModal from "./components/RenameValueModal";
import { IconButton } from "@/components/ui/icon-button";

const TRANSLATION_KEY_TABS = "settings.deals";

interface EditEditableSystemFieldDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fieldData: APIFieldItem | null;
  entityType: EntityType;
  onSave?: (fieldData?: any) => void;
}

export const EditEditableSystemFieldDrawer: React.FC<
  EditEditableSystemFieldDrawerProps
> = ({ isOpen, onOpenChange, fieldData, entityType, onSave }) => {
  const { t } = useTranslation();

  const {
    formState,
    isFormValid,
    formRef,
    formSetValue,
    setFormSetValue,
    setShowHelpText,
    setShowInCreation,
    setIsRequired,
    handleFormValid,
    handleOptionsManagerChange,
    handleHiddenToggle,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting,
    renamePrompt,
    resolveRename
  } = useEditableSystemFieldForm({
    fieldData,
    entityType: entityType
  });

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSave = async () => {
    try {
      await handleFormSubmit();
      onSave?.();
      handleClose();
    } catch (error) {
      // Rename-in-use prompt cancelled — abort quietly (no toast), drawer stays open.
      if (error instanceof Error && error.message === RENAME_CANCELLED) {
        return;
      }
      const backendDetail =
        isAxiosError(error) && typeof error.response?.data?.detail === "string"
          ? error.response.data.detail
          : null;
      toast.error(
        backendDetail ??
          t(`${TRANSLATION_KEY_TABS}.fieldManagement.updateField.error`)
      );
    }
  };

  const getTitle = () => {
    return (
      <CustomDrawerTitle
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.drawer.editField`)}
        subtitle={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.editableSystemField.subtitle`
        )}
      />
    );
  };

  if (!fieldData) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={onOpenChange}
      direction="right"
      modal={false}
      dismissible={false}
    >
      <AppDrawerContent isSettingsDrawer>
        <DrawerHeader>
          <DrawerTitle>
            {getTitle()}
          </DrawerTitle>
          <IconButton
            data-test="close-edit-editable-system-field-drawer"
            onClick={handleClose}
            variant="iconNoBg"
            size="md"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <FieldFormContent
          formData={formState.formData}
          optionsManagerData={formState.optionsManagerData}
          fieldData={fieldData}
          formSetValue={formSetValue}
          showHelpText={formState.showHelpText}
          showInCreation={formState.showInCreation}
          isRequired={formState.isRequired}
          onShowHelpTextChange={setShowHelpText}
          onShowInCreationChange={setShowInCreation}
          onRequiredChange={setIsRequired}
          onOptionsManagerChange={handleOptionsManagerChange}
          onHiddenToggle={
            fieldData.fieldKey === INDUSTRY_FIELD_KEY ||
            fieldData.fieldKey === SUB_INDUSTRY_FIELD_KEY
              ? handleHiddenToggle
              : undefined
          }
          onFormValid={handleFormValid}
          onSubmit={handleSave}
          formRef={formRef}
          onSetValue={setFormSetValue}
          isEditMode={true}
          isPresetFieldEdit={false}
          isEditableSystemField={true}
          entityType={entityType}
        />

        <FieldFormFooter
          isFormValid={isFormValid}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmitButton}
          onCancel={handleClose}
          isEditMode={true}
        />

        {renamePrompt && (
          <RenameValueModal
            open
            oldName={renamePrompt.oldName}
            newName={renamePrompt.newName}
            onCancel={() => resolveRename("cancel")}
            onConfirm={(strategy) => resolveRename(strategy)}
          />
        )}
      </AppDrawerContent>
    </Drawer>
  );
};
